import { Component, Input, Output, EventEmitter, OnChanges, computed, output, inject } from '@angular/core';
import { Data2Service } from '@/common/data2.service';
import { DomSanitizer } from '@angular/platform-browser';
import { TourismEvent, TourismEventSavePayload } from '@/tourism-events/tourism-events.model';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { DEFAULT_TOURISM_EVENT } from './default-tourism-event';
import { Permissions } from '@/common/enums/Permissions';
import { AuthService } from '@/auth/auth.service';
import { DateService } from '@/common/date.service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { deepClone } from '@/_utils/deep-clone';
import { getTimeDifferenceInMilliseconds } from '@/_utils/get-time-difference-in-milliseconds';
import { addMillisecondsToDate } from '@/_utils/add-milliseconds-to-date';
import { TourismEventSlots } from '@/bag/bag-transport-term/bag-transport-term.model';
import { TourismEventTransportTermSlotsStore } from '@/tourism-calendar/tourism-calendar-event-view/tourism-event-transport-term-slots-store';

interface Crew {
	id: number;
	name: string;
	phone: string;
	email: string;
}

@Component({
	selector: 'app-tourism-calendar-event-view',
	templateUrl: './tourism-calendar-event-view.component.html',
	styleUrls: ['./tourism-calendar-event-view.component.css'],
	providers: [provideNativeDateAdapter()],
})
export class TourismCalendarEventViewComponent implements OnChanges {
	@Input() selectedObject = {
		id: null,
		name: null,
		element: null,
	};
	@Input() vehicles;
	@Input() parameters;
	@Input() drivers;
	@Input() touristGuides;
	@Input() users;
	@Output() eventClosed = new EventEmitter();

	readonly reloadTourismData = output<void>();

	event: TourismEvent = DEFAULT_TOURISM_EVENT;
	links: any[];
	driverInformedDepartureHistoryLast = {
		date: null,
		userName: null,
	};
	driverInformedReturnHistoryLast = {
		date: null,
		userName: null,
	};
	guideInformedHistory = {
		date: null,
		userName: null,
	};
	guideInformedReturnHistory = {
		date: null,
		userName: null,
	};
	clientInformedHistory = {
		date: null,
		userName: null,
	};
	readyForPrintingHistory = {
		date: null,
		userName: null,
	};

	readonly isOnlyDispatcher = computed(() => {
		const enabledPermissions = Object.entries(this.auth.permissionsByCode()).filter((val) => val[1]);
		return enabledPermissions.length === 1 && enabledPermissions[0][0] === Permissions.DISPATCHER;
	});

	private tourismEventToSave: TourismEventSavePayload = null;
	readonly slotsStore = inject(TourismEventTransportTermSlotsStore);
	slots: TourismEventSlots = null;

	constructor(
		private readonly api: Data2Service,
		private readonly infobox: InfoboxService,
		private readonly sanitized: DomSanitizer,
		private readonly loader: LoaderService,
		private readonly auth: AuthService,
		private readonly datePipe: DatePipe,
		private readonly date: DateService,
	) {}

	ngOnChanges(): void {
		if (this.selectedObject.id == undefined || this.selectedObject.id == null) {
			return;
		}
		if (!this.vehicles) {
			return;
		}
		if (!this.parameters) {
			return;
		}
		if (!this.drivers) {
			return;
		}
		this.loadEvents();
		this.eventOpen();
	}

	guideInformed(guide_informed_type: 'guide_informed' | 'guide_informed_return'): void {
		if (!['guide_informed', 'guide_informed_return'].includes(guide_informed_type)) {
			throw new Error('Invalid guide_informed_type');
		}
		this.sendEvent(this.event.id, guide_informed_type, !this.event[guide_informed_type]);
	}

	readyForPrinting(): void {
		if (this.event.was_printed) {
			this.infobox.error('Nie można przełączyć na niegotowe do druku, jeśli druk już został wykonany!');
			return;
		}
		this.sendEvent(this.event.id, 'ready_for_printing', !this.event.ready_for_printing);
	}

	clientInformed(): void {
		this.sendEvent(this.event.id, 'client_informed', !this.event.client_informed);
	}

	eventClose(): void {
		this.eventClosed.emit(this.selectedObject.id);
		const element = document.getElementById('eventModal');
		element.style.display = 'none';
		element.className = 'modal fade';
	}

	driverInformed(type: string): void {
		this.event[type] = !this.event[type];
		const data = {
			event: this.event.id,
			type: type,
			value: this.event[type],
		};
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		this.api.post('/tourismEventsActions/', data, (_, error) => {
			if (error) {
				this.event[type] = !this.event[type];
				return;
			}
			this.loadEvents();
		});
	}
	updateEventDateTime(): void {
		this.loader.show();

		const updatedDateFrom = this.convertToPHPDateTimeFormat(this.event.date_from);
		const updatedDateTo = this.convertToPHPDateTimeFormat(this.event.date_to);

		this.updateDateFields(updatedDateFrom, updatedDateTo);

		this.syncRelatedProgramDates();
		this.saveEventUpdate();
	}

	private updateDateFields(updatedFrom: string, updatedTo: string): void {
		const currentFrom = this.tourismEventToSave.date_from;
		const currentTo = this.tourismEventToSave.date_to;

		const isDateFromChanged = currentFrom !== updatedFrom;
		const isDateToSame = currentTo === updatedTo;

		if (isDateFromChanged && isDateToSame) {
			const diff = getTimeDifferenceInMilliseconds(updatedFrom, currentFrom);
			const newDateTo = addMillisecondsToDate(currentTo, diff);
			const adjustedDateTo = this.convertToPHPDateTimeFormat(newDateTo.toISOString());

			this.tourismEventToSave.date_to = adjustedDateTo;
			this.event.date_to = adjustedDateTo;
		} else {
			this.tourismEventToSave.date_to = updatedTo;
		}

		this.tourismEventToSave.date_from = updatedFrom;
	}

	private syncRelatedProgramDates(): void {
		const { date_from, date_to } = this.tourismEventToSave;

		this.changeDriversProgramDatesIfNeeded(date_from, date_to);
		this.changeVehiclesDatesIfNeeded(date_from, date_to);
	}

	private saveEventUpdate(): void {
		this.api.put(`/tourismEvents/${this.event.id}/`, this.tourismEventToSave, (result, error) => {
			this.loader.hide();
			if (!!error) {
				return;
			}
			this.reloadTourismData.emit();
			this.eventClose();
			this.infobox.success('Zapisano!');
		});
	}

	private changeDriversProgramDatesIfNeeded(updatedDateFrom: string, updatedDateTo: string): void {
		const driversProgram = this.tourismEventToSave.drivers_program;
		if (driversProgram.length === 0) {
			return;
		}
		const firstProgram = driversProgram[0];
		const lastProgram = driversProgram[driversProgram.length - 1];

		this.updateDateIfNeeded(
			firstProgram,
			updatedDateFrom,
			true,
			(item, isStart) => (isStart ? item.date_from : item.date_to),
			(item, isStart, date) => {
				if (isStart) item.date_from = date;
				else item.date_to = date;
			},
		);

		this.updateDateIfNeeded(
			lastProgram,
			updatedDateTo,
			false,
			(item, isStart) => (isStart ? item.date_from : item.date_to),
			(item, isStart, date) => {
				if (isStart) item.date_from = date;
				else item.date_to = date;
			},
		);
	}

	private changeVehiclesDatesIfNeeded(updatedDateFrom: string, updatedDateTo: string): void {
		const vehicles = this.tourismEventToSave.vehicles;
		if (vehicles.length === 0) {
			return;
		}
		const firstVehicle = vehicles[0];
		const lastVehicle = vehicles[vehicles.length - 1];

		this.updateDateIfNeeded(
			firstVehicle,
			updatedDateFrom,
			true,
			(item, isStart) => (isStart ? item.dateFrom : item.dateTo),
			(item, isStart, date) => {
				if (isStart) (item.dateFrom as string) = date;
				else (item.dateTo as string) = date;
			},
		);

		this.updateDateIfNeeded(
			lastVehicle,
			updatedDateTo,
			false,
			(item, isStart) => (isStart ? item.dateFrom : item.dateTo),
			(item, isStart, date) => {
				if (isStart) (item.dateFrom as string) = date;
				else (item.dateTo as string) = date;
			},
		);
	}

	private updateDateIfNeeded<T>(
		obj: T,
		newDate: string,
		isStart: boolean,
		getDate: (item: T, isStart: boolean) => string | Date,
		setDate: (item: T, isStart: boolean, date: string) => void,
	): void {
		const isDateDifferent = (date1: Date | string, date2: Date | string): boolean =>
			new Date(date1).getTime() !== new Date(date2).getTime();

		if (isDateDifferent(getDate(obj, isStart), newDate)) {
			setDate(obj, isStart, newDate as `${string}-${string}-${string} ${string}:${string}:${string}`);
		}
	}

	private convertToPHPDateTimeFormat(dateString: string): string {
		return this.date.toUtcIso(dateString)?.replace('T', ' ').replace('Z', '') ?? '';
	}

	private sendEvent(event: number, type: string, value: boolean): Promise<void> {
		const data = { event, type, value };
		return new Promise((resolve, reject) => {
			this.api.post('/tourismEventsActions/', data, (result, error) => {
				if (error) {
					reject(result);
					return;
				}
				resolve(error);
				this.loadEvents();
			});
		});
	}

	private loadEvents(): void {
		this.loader.show();
		this.slots = this.slotsStore.slotMap.get(this.selectedObject.id);
		this.api.get('/tourismEvents/' + this.selectedObject.id + '/', (result) => {
			const clonedData = deepClone(result.data);

			const vehicles = clonedData.vehicles.map(({ vehicle_id, date_start, date_stop }) => ({
				id: vehicle_id,
				dateFrom: date_start,
				dateTo: date_stop,
			}));

			this.tourismEventToSave = {
				...clonedData,
				vehicles,
			};

			const vehicleNames = [];
			result.data.vehicles.forEach(({ vehicle_id }) => {
				const vehicle = this.vehicles.find((v) => v.id === vehicle_id);
				if (vehicle) {
					vehicleNames.push(vehicle.name);
				}
			});
			result.data.vehicle = vehicleNames.join(', ');

			const status = this.parameters.status.find((obj) => obj.id == result.data.status);
			const category = this.parameters.category.find((obj) => obj.id == result.data.category);
			result.data.category = category?.name;
			result.data.status = status?.name;

			result.data.text_departure = this.sanitized.bypassSecurityTrustHtml(result.data.text_departure);
			result.data.text_return = this.sanitized.bypassSecurityTrustHtml(result.data.text_return);
			result.data.text_drivers = this.sanitized.bypassSecurityTrustHtml(result.data.text_drivers);
			result.data.text_organization = this.sanitized.bypassSecurityTrustHtml(result.data.text_organization);
			result.data.text_client = this.sanitized.bypassSecurityTrustHtml(result.data.text_client);
			result.data.text_refueling = this.sanitized.bypassSecurityTrustHtml(result.data.text_refueling);
			result.data.text_billing = this.sanitized.bypassSecurityTrustHtml(result.data.text_billing);
			result.data.text_notes = this.sanitized.bypassSecurityTrustHtml(result.data.text_notes);
			result.data.drivers_departure = this.convertDrivers(result.data.drivers_departure);
			result.data.drivers_program = this.convertDrivers(result.data.drivers_program);
			result.data.drivers_return = this.convertDrivers(result.data.drivers_return);
			result.data.tourist_guides_departure = this.convertTouristGuides(result.data.tourist_guides_departure);
			result.data.tourist_guides_program = this.convertTouristGuides(result.data.tourist_guides_program);
			result.data.tourist_guides_return = this.convertTouristGuides(result.data.tourist_guides_return);
			result.data.owner = this.convertOwner(result.data.owner);
			result.data.driver_informed_departure = result.data.driver_informed_departure === '1';
			result.data.driver_informed_return = result.data.driver_informed_return === '1';
			result.data.guide_informed = result.data.guide_informed === '1';
			result.data.guide_informed_return = result.data.guide_informed_return === '1';
			result.data.client_informed = result.data.client_informed === '1';
			result.data.was_printed = result.data.was_printed === '1';
			result.data.ready_for_printing = result.data.ready_for_printing === '1';
			this.driverInformedDepartureHistoryLast = {
				date: null,
				userName: null,
			};
			this.driverInformedReturnHistoryLast = {
				date: null,
				userName: null,
			};
			this.guideInformedHistory = { date: null, userName: null };
			this.guideInformedReturnHistory = { date: null, userName: null };
			this.clientInformedHistory = { date: null, userName: null };
			this.readyForPrintingHistory = { date: null, userName: null };
			if (result.data.driverInformedDepartureHistoryLast) {
				this.driverInformedDepartureHistoryLast.date = result.data.driverInformedDepartureHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.driverInformedDepartureHistoryLast.user);
				this.driverInformedDepartureHistoryLast.userName = user ? user.name : '???';
			}
			if (result.data.driverInformedReturnHistoryLast) {
				this.driverInformedReturnHistoryLast.date = result.data.driverInformedReturnHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.driverInformedReturnHistoryLast.user);
				this.driverInformedReturnHistoryLast.userName = user ? user.name : '???';
			}
			if (result.data.guideInformedHistoryLast) {
				this.guideInformedHistory.date = result.data.guideInformedHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.guideInformedHistoryLast.user);
				this.guideInformedHistory.userName = user ? user.name : '???';
			}
			if (result.data.guideInformedReturnHistoryLast) {
				this.guideInformedReturnHistory.date = result.data.guideInformedReturnHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.guideInformedReturnHistoryLast.user);
				this.guideInformedReturnHistory.userName = user ? user.name : '???';
			}
			if (result.data.clientInformedHistoryLast) {
				this.clientInformedHistory.date = result.data.clientInformedHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.clientInformedHistoryLast.user);
				this.clientInformedHistory.userName = user ? user.name : '???';
			}
			if (result.data.readyForPrintingHistoryLast) {
				this.readyForPrintingHistory.date = result.data.readyForPrintingHistoryLast?.date;
				const user = this.users.find((obj) => obj.id == result.data.readyForPrintingHistoryLast.user);
				this.readyForPrintingHistory.userName = user ? user.name : '???';
			}
			result.data.tasks = this.convertTaskContent(result);
			this.event = result.data;
			this.loader.hide();
		});
		this.api.get('/tourismEventsLinks/' + this.selectedObject.id + '/', (result, error) => {
			this.links = result.data;
		});
	}

	private convertTaskContent(result): string[] {
		const arr = [];
		result.data.tasks?.forEach((task) => {
			arr.push(task.content.replace('<p>', '').split('</p>')[0]);
		});
		return arr;
	}

	private convertTouristGuides(crew: Crew[]): Crew[] {
		const newCrew = [];
		crew?.forEach((person) => {
			const findObj = this.touristGuides.find((obj) => obj.id == person);
			if (!findObj) {
				newCrew.push({ id: null, name: '?' });
			} else {
				findObj.phoneBusiness = findObj.phoneBusiness ? ' ' + findObj.phoneBusiness : '';
				findObj.emailBusiness = findObj.emailBusiness ? ' ' + findObj.emailBusiness : '';
				newCrew.push({
					id: findObj.id,
					name: findObj.lastName + ' ' + findObj.firstName,
					phone: findObj.phonePrivate + findObj.phoneBusiness,
					email: findObj.emailPrivate + findObj.emailBusiness,
				});
			}
		});
		return newCrew;
	}

	private convertOwner(driver: Crew[]): unknown[] {
		const newDriver = [];
		driver?.forEach((person) => {
			const findObj = this.users.find((obj) => obj.id == person);
			if (!findObj) {
				newDriver.push({ id: null, name: '?' });
			} else {
				newDriver.push({
					id: findObj.id,
					name: findObj.lastName + ' ' + findObj.firstName,
				});
			}
		});
		return newDriver;
	}

	private convertDrivers(driver: (Crew & { driver: number })[]): Crew[] {
		const newDrivers = [];
		driver?.forEach((driver, driverIndex) => {
			const findDriver = this.drivers.find((obj) => obj.id == driver.driver);
			if (!findDriver) {
				newDrivers[driverIndex] = {
					id: null,
					name: '?',
					phone: '',
					email: '',
				};
				return;
			}
			findDriver.phone_business = findDriver.phone_business ? ' ' + findDriver.phone_business : '';
			findDriver.email_business = findDriver.email_business ? ' ' + findDriver.email_business : '';
			newDrivers[driverIndex] = {
				id: findDriver.id,
				name: findDriver.last_name + ' ' + findDriver.first_name,
				phone: findDriver.phone_business + ' / ' + findDriver.phone_private,
				email: findDriver.email_business + ' / ' + findDriver.email_private,
			};
		});
		return newDrivers;
	}

	private eventOpen(): void {
		const element = document.getElementById('eventModal');
		element.style.display = 'block';
		element.style.paddingRight = '17px';
		element.className = 'modal fade show';
	}
}
